import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getWhatsappMessageQueriesApi,
  postWhatsappMessageQueryApi,
  updateWhatsappMessageQueryStatusApi,
  enableWhatsappMessageAiResponseApi,
  postWhatsappProductFlowApi,
  verifyWhatsappWebhookApi,
  postWhatsappWebhookApi,
  sendWhatsappMessageApi,
} from "../../api/ecommerce/WhatsappApi";

const initialState = {
  messageQueries: [],
  loading: false,
  messageQuery: null,
  postingMessageQuery: false,
  queryStatus: null,
  updatingQueryStatus: false,
  aiResponse: null,
  enablingAiResponse: false,
  productFlow: null,
  creatingProductFlow: false,
  webhookVerification: null,
  verifyingWebhook: false,
  webhookResponse: null,
  postingWebhook: false,
  sentMessage: null,
  sendingMessage: false,
  error: null,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const getWhatsappMessageQueries = createAsyncThunk(
  "whatsapp/getWhatsappMessageQueries",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getWhatsappMessageQueriesApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const postWhatsappMessageQuery = createAsyncThunk(
  "whatsapp/postWhatsappMessageQuery",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postWhatsappMessageQueryApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const updateWhatsappMessageQueryStatus = createAsyncThunk(
  "whatsapp/updateWhatsappMessageQueryStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateWhatsappMessageQueryStatusApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const enableWhatsappMessageAiResponse = createAsyncThunk(
  "whatsapp/enableWhatsappMessageAiResponse",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await enableWhatsappMessageAiResponseApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const postWhatsappProductFlow = createAsyncThunk(
  "whatsapp/postWhatsappProductFlow",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postWhatsappProductFlowApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const verifyWhatsappWebhook = createAsyncThunk(
  "whatsapp/verifyWhatsappWebhook",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await verifyWhatsappWebhookApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

// Dispatch with { tenant, body }; body is the webhook JSON payload.
export const postWhatsappWebhook = createAsyncThunk(
  "whatsapp/postWhatsappWebhook",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postWhatsappWebhookApi(payload.tenant, payload.body);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const sendWhatsappMessage = createAsyncThunk(
  "whatsapp/sendWhatsappMessage",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await sendWhatsappMessageApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    clearWhatsappError: (state) => {
      state.error = null;
    },
    clearWhatsappState: () => initialState,
  },
  extraReducers: (builder) => {
    const operations = [
      [getWhatsappMessageQueries, "messageQueries", "loading"],
      [postWhatsappMessageQuery, "messageQuery", "postingMessageQuery"],
      [updateWhatsappMessageQueryStatus, "queryStatus", "updatingQueryStatus"],
      [enableWhatsappMessageAiResponse, "aiResponse", "enablingAiResponse"],
      [postWhatsappProductFlow, "productFlow", "creatingProductFlow"],
      [verifyWhatsappWebhook, "webhookVerification", "verifyingWebhook"],
      [postWhatsappWebhook, "webhookResponse", "postingWebhook"],
      [sendWhatsappMessage, "sentMessage", "sendingMessage"],
    ];

    operations.forEach(([thunk, dataKey, loadingKey]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[loadingKey] = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[loadingKey] = false;
          state[dataKey] = action.payload?.response ?? action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state[loadingKey] = false;
          state.error =
            action.payload?.errorMessage ||
            action.error?.message ||
            "Something went wrong";
        });
    });
  },
});

export const { clearWhatsappError, clearWhatsappState } = whatsappSlice.actions;

export default whatsappSlice.reducer;

